import {
  EmailAuthProvider,
  GoogleAuthProvider,
  User,
  getAuth,
  getRedirectResult,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  reload,
  sendEmailVerification,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updatePassword,
} from 'firebase/auth';
import { AuthenticatedUser } from '../domain/auth';
import { logger } from './logger';

const mapUser = (user: User): AuthenticatedUser => ({
  uid: user.uid,
  email: user.email,
  emailVerified: user.emailVerified === true,
  providerIds: Array.from(
    new Set((user.providerData || []).map(({ providerId }) => providerId)),
  ).sort(),
});

const verificationSettings = () => ({
  url: window.location.origin,
  handleCodeInApp: false,
});

// Registration creates the account before the verification email is sent, and
// the login screen unmounts as soon as the new user signs in. A failed send is
// therefore recorded here for the verification screen instead of thrown.
let verificationDeliveryFailedUid: string | null = null;
const verificationDeliveryListeners = new Set<() => void>();

const setVerificationDeliveryFailed = (uid: string | null) => {
  if (verificationDeliveryFailedUid === uid) return;
  verificationDeliveryFailedUid = uid;
  verificationDeliveryListeners.forEach((listener) => listener());
};

const currentFirebaseUser = (): User => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('No account is currently signed in.');
  return user;
};

export const authService = {
  currentUser: (): AuthenticatedUser | null => {
    const user = getAuth().currentUser;
    return user ? mapUser(user) : null;
  },
  subscribe: (listener: (user: AuthenticatedUser | null) => void) =>
    onAuthStateChanged(getAuth(), (user) =>
      listener(user ? mapUser(user) : null),
    ),
  signIn: (email: string, password: string) =>
    import('firebase/auth').then(({ signInWithEmailAndPassword }) =>
      signInWithEmailAndPassword(getAuth(), email, password),
    ),
  async register(email: string, password: string): Promise<void> {
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    const credential = await createUserWithEmailAndPassword(
      getAuth(),
      email,
      password,
    );
    try {
      await sendEmailVerification(credential.user, verificationSettings());
      setVerificationDeliveryFailed(null);
    } catch (cause) {
      logger.warn('Verification email was not sent after registration:', cause);
      setVerificationDeliveryFailed(credential.user.uid);
    }
  },
  async resendVerification(): Promise<void> {
    const user = currentFirebaseUser();
    if (user.emailVerified) return;
    await sendEmailVerification(user, verificationSettings());
    setVerificationDeliveryFailed(null);
  },
  verificationDeliveryFailed: (uid: string): boolean =>
    verificationDeliveryFailedUid === uid,
  subscribeVerificationDelivery: (listener: () => void) => {
    verificationDeliveryListeners.add(listener);
    return () => {
      verificationDeliveryListeners.delete(listener);
    };
  },
  async refreshCurrentUser(): Promise<AuthenticatedUser> {
    const user = currentFirebaseUser();
    await reload(user);
    if (user.emailVerified) await user.getIdToken(true);
    return mapUser(user);
  },
  // Firebase reloads the persisted profile on startup without refreshing the
  // ID token, so a user verified from the email link can arrive with
  // emailVerified=true but a token whose email_verified claim is still false.
  // A missing or different current user means a newer auth change will
  // initialize its own session, so that is not reported as stale.
  async hasStaleVerificationToken(expectedUid: string): Promise<boolean> {
    const user = getAuth().currentUser;
    if (!user || user.uid !== expectedUid) return false;
    const { claims } = await user.getIdTokenResult();
    return claims.email_verified !== true;
  },
  async signInWithGooglePopup(): Promise<void> {
    await signInWithPopup(getAuth(), new GoogleAuthProvider());
  },
  async signInWithGoogleRedirect(): Promise<void> {
    await signInWithRedirect(getAuth(), new GoogleAuthProvider());
  },
  async completeGoogleRedirect(): Promise<void> {
    await getRedirectResult(getAuth());
  },
  async linkGoogle(): Promise<AuthenticatedUser> {
    const credential = await linkWithPopup(
      currentFirebaseUser(),
      new GoogleAuthProvider(),
    );
    await credential.user.getIdToken(true);
    return mapUser(credential.user);
  },
  async reauthenticate(
    expectedUid: string,
    method: 'password' | 'google.com',
    password = '',
  ): Promise<void> {
    const user = currentFirebaseUser();
    if (user.uid !== expectedUid)
      throw new Error('The signed-in account changed.');
    if (!user.providerData.some(({ providerId }) => providerId === method)) {
      throw new Error('This sign-in method is not linked to your account.');
    }
    if (method === 'password') {
      if (!user.email)
        throw new Error('The signed-in account has no email address.');
      await reauthenticateWithCredential(
        user,
        EmailAuthProvider.credential(user.email, password),
      );
    } else {
      await reauthenticateWithPopup(user, new GoogleAuthProvider());
    }
    if (getAuth().currentUser?.uid !== expectedUid)
      throw new Error('The signed-in account changed.');
    await user.getIdToken(true);
  },
  signOut: () => signOut(getAuth()),
  async changePassword(
    user: AuthenticatedUser,
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    if (!user.providerIds.includes('password'))
      throw new Error('This account does not use a password.');
    if (!user.email)
      throw new Error('The signed-in account has no email address.');
    const firebaseUser = currentFirebaseUser();
    if (firebaseUser.uid !== user.uid)
      throw new Error('The signed-in account changed.');
    await reauthenticateWithCredential(
      firebaseUser,
      EmailAuthProvider.credential(user.email, currentPassword),
    );
    await updatePassword(firebaseUser, newPassword);
  },
};
